/**
 * :func:`SseProvider` — wraps :func:`useSse` so any descendant can
 * read the connection state via :func:`useSseConnection`. Owns the
 * map-viewport bridge so the Map (deep in the route tree) can push
 * its bbox without re-mounting the SSE connection.
 *
 * Stream A PR 2 / commit M3 — split from
 * :file:`sse-context.ts` per the fast-refresh discipline (the
 * ``.tsx`` module exports the component exclusively; the ``.ts``
 * sibling owns the context value + the access hook).
 *
 * # Provider placement
 *
 * Mounted in :file:`main.tsx` **above** :class:`RouterProvider`, so
 * both the chrome (:class:`TopBar`'s
 * :class:`ConnectionStatusIndicator`) and the routed pages
 * (:class:`Map`'s :class:`SnapshotLayer`) can read the connection
 * state. The viewport is ``null`` at mount; the Map's
 * ``onMapReady`` callback calls :func:`useSetMapViewport` with the
 * initial bounds; the provider re-runs :func:`useSse` against the
 * new viewport.
 *
 * Future Stream C (multi-pane) work may hoist additional consumers
 * — e.g., an Events panel that subscribes to the same stream.
 * Because the provider is already above the route tree, no
 * restructuring is needed at that point.
 *
 * Cross-references:
 *   - REQ-UI-006 / REQ-UI-008 / REQ-UI-009.
 *   - ADR-016 §1 + §3 (snapshot+SSE).
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md.
 */
import { useMemo, useState, type ReactNode } from "react";
import { MapViewportContext, SseContext } from "@/map/sse-context";
import { useSse, type SseViewport } from "@/map/use-sse";

/**
 * Props for :func:`SseProvider`.
 *
 * The provider is parameter-free in production; tests pass
 * ``eventSourceFactory`` to inject a fake :class:`EventSource`.
 */
export interface SseProviderProps {
  /** Optional :class:`EventSource` factory override — tests inject
   *  a fake. Production code omits. */
  eventSourceFactory?: (url: string) => EventSource;
  /** Optional initial viewport for tests that want to skip the
   *  Map's ``onMapReady`` bootstrap. Production code omits. */
  initialViewport?: SseViewport | null;
  children: ReactNode;
}

/**
 * Connect to the BFF SSE stream + expose the connection state to
 * descendants. Owns the map-viewport bridge — the Map pushes its
 * bbox via :func:`useSetMapViewport`; the provider passes that
 * viewport to :func:`useSse`.
 *
 * Cross-references:
 *   - REQ-UI-006 / REQ-UI-008 / REQ-UI-009.
 */
export function SseProvider({
  eventSourceFactory,
  initialViewport = null,
  children,
}: SseProviderProps): React.ReactElement {
  const [viewport, setViewport] = useState<SseViewport | null>(initialViewport);

  // Memoise the viewport-context value so consumers don't re-render
  // every time the SseProvider does. The setter is stable across
  // renders (React guarantees this for ``useState`` setters); only
  // the viewport changes drive the memo invalidation.
  const viewportContextValue = useMemo(() => ({ viewport, setViewport }), [viewport]);

  const connection = useSse({ viewport, eventSourceFactory });

  return (
    <MapViewportContext.Provider value={viewportContextValue}>
      <SseContext.Provider value={connection}>{children}</SseContext.Provider>
    </MapViewportContext.Provider>
  );
}
